import React, { useEffect, useState } from 'react';

/**
 * Structured missing-field questions after template generation.
 * Answers are submitted as a single structured chat message.
 */
export default function OpsTemplateQuestionPanel({
  questions = [],
  onSubmitAnswers,
  onSkip,
  disabled = false
}) {
  const list = Array.isArray(questions) ? questions : [];
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    setAnswers({});
  }, [list.map((q) => q.id).join('|')]);

  if (!list.length) return null;

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const toggleMulti = (id, option) => {
    const current = Array.isArray(answers[id]) ? answers[id] : [];
    const next = current.includes(option)
      ? current.filter((x) => x !== option)
      : [...current, option];
    setAnswer(id, next);
  };

  const handleSubmit = () => {
    const payload = list
      .map((q) => {
        const raw = answers[q.id];
        let value = '';
        if (Array.isArray(raw)) value = raw.join(', ');
        else if (typeof raw === 'boolean') value = raw ? 'yes' : 'no';
        else value = String(raw ?? '').trim();
        if (!value && q.required) return null;
        if (!value) value = 'Use best judgement';
        return { id: q.id, field: q.field, question: q.question, value };
      })
      .filter(Boolean);

    if (!payload.length) return;
    onSubmitAnswers?.(payload);
  };

  const requiredMissing = list.some((q) => {
    if (!q.required) return false;
    const raw = answers[q.id];
    if (Array.isArray(raw)) return raw.length === 0;
    return !String(raw ?? '').trim();
  });

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3">
      <p className="text-xs font-semibold text-amber-950">Missing / confirm</p>
      <p className="mb-2 text-[10px] text-amber-800">
        Answer what you can — we will refine the proposal.
      </p>
      <div className="max-h-52 space-y-3 overflow-y-auto">
        {list.map((q) => (
          <div key={q.id} className="rounded-lg border border-amber-100 bg-white/80 p-2">
            <p className="text-xs font-medium text-slate-800">
              {q.question}
              {q.required ? <span className="text-red-500"> *</span> : null}
            </p>
            {q.reason ? <p className="mt-0.5 text-[10px] text-slate-500">{q.reason}</p> : null}

            {q.inputType === 'boolean' ? (
              <div className="mt-1.5 flex gap-2">
                {['yes', 'no'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    disabled={disabled}
                    onClick={() => setAnswer(q.id, opt === 'yes')}
                    className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                      answers[q.id] === (opt === 'yes')
                        ? 'bg-amber-600 text-white'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            ) : q.inputType === 'single_select' && Array.isArray(q.options) ? (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {q.options.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    disabled={disabled}
                    onClick={() => setAnswer(q.id, opt)}
                    className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                      answers[q.id] === opt
                        ? 'bg-amber-600 text-white'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            ) : q.inputType === 'multi_select' && Array.isArray(q.options) ? (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {q.options.map((opt) => {
                  const selected = Array.isArray(answers[q.id]) && answers[q.id].includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleMulti(q.id, opt)}
                      className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                        selected ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            ) : (
              <input
                disabled={disabled}
                value={answers[q.id] || ''}
                onChange={(e) => setAnswer(q.id, e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-200 px-2 py-1 text-xs"
                placeholder="Your answer"
              />
            )}
          </div>
        ))}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || requiredMissing}
          onClick={handleSubmit}
          className="rounded-lg bg-amber-700 px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-40"
        >
          Submit answers
        </button>
        {onSkip ? (
          <button
            type="button"
            disabled={disabled}
            onClick={onSkip}
            className="text-[10px] font-semibold text-slate-600 underline"
          >
            Skip for now
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function formatAnswersMessage(answers) {
  const lines = (answers || []).map((a) => `- ${a.id}: ${a.value}`);
  return `[Answers]\n${lines.join('\n')}`;
}
